const passedCheck = (id, title, summary, findings = []) => ({
    id,
    title,
    status: 'passed',
    summary,
    findings,
});

const cyberSecurityData = {
    title: 'Security Checks',
    risk: 'Medium',
    riskDescription: 'Some indicators require additional verification before proceeding.',
    categories: [
        {
            id: 'domain-ownership',
            title: 'Domain & Ownership',
            icon: 'ri-global-line',
            iconColor: 'info',
            status: 'warning',
            description: 'The domain appears legitimate, but some ownership details require further verification.',
            whyItMatters: 'Domain and ownership information helps verify the legitimacy of the carrier’s digital identity. Limited or inconsistent information can be a risk indicator and may require additional verification.',
            checks: [
                passedCheck('whois', 'WHOIS Check', 'WHOIS data is available.', [
                    { label: 'WHOIS record', value: 'Available' },
                    { label: 'Privacy protection', value: 'Enabled' },
                ]),
                passedCheck('domain-age', 'Domain Age & History', 'No concerning domain age or historical changes detected.', [
                    { label: 'Domain age', value: 'Established' },
                    { label: 'Historical changes', value: 'None concerning' },
                ]),
                {
                    id: 'registrar',
                    title: 'Registrar Information',
                    status: 'warning',
                    summary: 'Registrar information requires additional verification.',
                    headline: 'Registrar information requires additional verification.',
                    whyItMatters: 'Recent changes in registrar information can indicate potential risk, such as a transfer of ownership or other modifications that require further verification.',
                    evidence: 'View detailed technical data (e.g. WHOIS record)',
                    findings: [
                        { label: 'Registrar', value: 'Available' },
                        { label: 'Registration information', value: 'Available' },
                        { label: 'Recent changes', value: 'Detected', tone: 'warning' },
                    ],
                },
                passedCheck('domain-legitimacy', 'Domain Legitimacy', 'Domain signals appear legitimate.', [
                    { label: 'Naming consistency', value: 'Consistent' },
                    { label: 'Risk signals', value: 'None' },
                ]),
            ],
        },
        {
            id: 'email-authentication',
            title: 'Email Authentication',
            icon: 'ri-mail-line',
            iconColor: 'warning',
            status: 'passed',
            description: 'Email authentication is properly configured.',
            whyItMatters: 'Proper email authentication reduces the risk of email spoofing and helps verify that the carrier’s email domain is legitimate.',
            successTitle: 'All email authentication checks passed',
            successDescription: 'SPF, DKIM, DMARC and MX records are properly configured for this domain.',
            meaning: [
                'Emails from this domain are properly authenticated.',
                'Reduces the risk of email spoofing.',
                'Helps confirm the legitimacy of the carrier’s digital identity.',
            ],
            checks: [
                passedCheck('spf', 'SPF Check', 'SPF is properly configured.', [{ label: 'SPF record', value: 'Configured' }]),
                passedCheck('dkim', 'DKIM Check', 'DKIM is properly configured.', [{ label: 'DKIM record', value: 'Configured' }]),
                passedCheck('dmarc', 'DMARC Check', 'DMARC is properly configured.', [{ label: 'DMARC policy', value: 'Configured' }]),
                passedCheck('mx', 'MX Record Check', 'Mail records are properly configured.', [{ label: 'MX records', value: 'Configured' }]),
            ],
        },
        {
            id: 'dns-infrastructure',
            title: 'DNS & Infrastructure',
            icon: 'ri-server-line',
            iconColor: 'muted',
            status: 'passed',
            description: 'DNS and hosting infrastructure appear consistent and reachable.',
            whyItMatters: 'DNS and infrastructure checks confirm that the carrier’s digital presence is stable and not hosted on disposable or high-risk infrastructure.',
            checks: [
                passedCheck('dns-infrastructure', 'DNS & Infrastructure', 'Core DNS and hosting infrastructure is reachable.', [
                    { label: 'Infrastructure', value: 'Reachable' },
                ]),
                passedCheck('dns-record-primary', 'DNS Record Check', 'Primary DNS records resolve correctly.', [
                    { label: 'A / AAAA records', value: 'Resolved' },
                ]),
                passedCheck('dns-record-secondary', 'DNS Record Check', 'Authoritative name-server records resolve correctly.', [
                    { label: 'NS records', value: 'Resolved' },
                ]),
            ],
        },
        {
            id: 'reputation',
            title: 'Reputation & Threat Intel',
            icon: 'ri-shield-check-line',
            iconColor: 'success',
            status: 'passed',
            description: 'No active threat-intelligence matches were found for this domain.',
            whyItMatters: 'Reputation and threat intel sources help identify domains already associated with abuse, malware, or fraud.',
            checks: [
                passedCheck('domain-reputation', 'Domain reputation', 'Domain reputation is within an acceptable range.', [
                    { label: 'Domain reputation', value: 'Acceptable' },
                ]),
                passedCheck('ip-reputation', 'IP reputation', 'Hosting IP reputation is clean.', [
                    { label: 'IP reputation', value: 'Clean' },
                ]),
                passedCheck('security-databases', 'Security databases', 'No matches in monitored security databases.', [
                    { label: 'Database matches', value: 'None' },
                ]),
                passedCheck('malware-indicators', 'Malware indicators', 'No malware indicators were found.', [
                    { label: 'Malware indicators', value: 'None' },
                ]),
                passedCheck('phishing-indicators', 'Phishing indicators', 'No phishing indicators were found.', [
                    { label: 'Phishing indicators', value: 'None' },
                ]),
                passedCheck('blacklist-status', 'Blacklist status', 'The domain is not listed on monitored blacklists.', [
                    { label: 'Blacklist status', value: 'Not listed' },
                ]),
            ],
        },
        {
            id: 'impersonation',
            title: 'Impersonation & Spoofing',
            icon: 'ri-links-line',
            iconColor: 'warning',
            status: 'warning',
            description: 'A domain similar to a trusted organization was identified and requires review.',
            whyItMatters: 'Lookalike domains and spoofing patterns are commonly used to impersonate trusted carriers.',
            checks: [
                {
                    id: 'lookalike',
                    title: 'Potential lookalike domain detected',
                    status: 'warning',
                    summary: 'A domain similar to a trusted organization was identified and requires review.',
                    headline: 'Potential lookalike domain detected',
                    whyItMatters: 'Lookalike domains can be used to impersonate a trusted carrier or logistics brand.',
                    evidence: 'View similar domain matches',
                    findings: [
                        { label: 'Similar domain', value: 'Detected', tone: 'warning' },
                        { label: 'Review required', value: 'Yes', tone: 'warning' },
                    ],
                },
                {
                    id: 'email-routing',
                    title: 'Suspicious email routing detected',
                    status: 'critical',
                    summary: 'The email routing information is inconsistent with the expected sender and may indicate a potential spoofing attempt.',
                    headline: 'Suspicious email routing detected',
                    whyItMatters: 'Unexpected routing paths can indicate spoofing or an unauthorized sending infrastructure.',
                    evidence: 'View email header trace',
                    findings: [
                        { label: 'Expected sender', value: 'Not matched', tone: 'danger' },
                        { label: 'Routing path', value: 'Inconsistent', tone: 'danger' },
                    ],
                },
            ],
        },
        {
            id: 'authentication-consistency',
            title: 'Authentication Consistency',
            icon: 'ri-equalizer-line',
            iconColor: 'muted',
            status: 'warning',
            description: 'Some sender, authentication, and website information show inconsistencies.',
            whyItMatters: 'Consistency across sender identity, authentication, and website details supports a legitimate digital identity.',
            checks: [
                {
                    id: 'sender-consistency',
                    title: 'Sender information is not fully consistent',
                    status: 'warning',
                    summary: 'Some sender, authentication, and website information show inconsistencies and require further review.',
                    headline: 'Sender information is not fully consistent',
                    whyItMatters: 'Inconsistent sender and website details can indicate a fragmented or spoofed digital identity.',
                    findings: [
                        { label: 'Sender identity', value: 'Partially consistent', tone: 'warning' },
                        { label: 'Website identity', value: 'Review required', tone: 'warning' },
                    ],
                },
            ],
        },
    ],
};

const flattenChecks = () => cyberSecurityData.categories.flatMap((category) => category.checks);

cyberSecurityData.summary = {
    passed: flattenChecks().filter((check) => check.status === 'passed').length,
    warnings: flattenChecks().filter((check) => check.status === 'warning').length,
    failed: flattenChecks().filter((check) => check.status === 'failed' || check.status === 'critical').length,
};

cyberSecurityData.overviewFindings = [
    {
        id: 'email-routing',
        categoryId: 'impersonation',
        title: 'Suspicious email routing detected',
        category: 'Impersonation & Spoofing',
        status: 'critical',
        summary: 'The email routing information is inconsistent with the expected sender and may indicate a potential spoofing attempt.',
    },
    {
        id: 'registrar',
        categoryId: 'domain-ownership',
        title: 'Registrar information requires additional verification',
        category: 'Domain & Ownership',
        status: 'warning',
        summary: 'Some domain ownership details could not be fully verified.',
    },
    {
        id: 'lookalike',
        categoryId: 'impersonation',
        title: 'Potential lookalike domain detected',
        category: 'Impersonation & Spoofing',
        status: 'warning',
        summary: 'A domain similar to a trusted organization was identified and requires review.',
    },
    {
        id: 'sender-consistency',
        categoryId: 'authentication-consistency',
        title: 'Sender information is not fully consistent',
        category: 'Authentication Consistency',
        status: 'warning',
        summary: 'Some sender, authentication, and website information show inconsistencies and require further review.',
    },
];

export const getCyberSecurityCategory = (categoryId) => (
    cyberSecurityData.categories.find((category) => category.id === categoryId) || null
);

export const getCyberSecurityCheck = (categoryId, checkId) => {
    const category = getCyberSecurityCategory(categoryId);
    const categoryCheck = category?.checks.find((check) => check.id === checkId);

    if (categoryCheck) return { category, check: categoryCheck };

    return { category, check: null };
};

export default cyberSecurityData;
